
import {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  Ref,
  ref,
  SetupContext,
  watch,
} from 'vue';
import type { FilterSegment, Period } from '@/types/flow-types';
import { useI18n } from 'vue-i18n';
import {
  addInArraySegment,
  getActiveSegments,
  removeInSegment,
} from '@/composables/flows/useFlowSegments';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { asInt } from '@/helpers';
import getSegments from '@/components/flows/operators/filters/CurrentCart/segments';
import {
  ButtonGroup,
  PeriodSelector,
  FilterGlobalInformations,
  FilterPanelContent,
  FilterSegmentForm,
} from '@/components/flows/operators/shared';
import { TypeCampaignEnum } from '@/types';
import type { CurrentCartData, CurrentCartFilters } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'FilterCurrentCartForm',

  components: {
    FieldErrors,
    FilterGlobalInformations,
    FilterPanelContent,
    ButtonGroup,
    PeriodSelector,
    FilterSegmentForm,
  },

  props: {
    modelValue: {
      type: Object as PropType<CurrentCartData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    campaignType: {
      type: String as PropType<TypeCampaignEnum>,
      default: TypeCampaignEnum.AUTOMATION,
    },

    operatorId: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: CurrentCartData; formValidation: any; campaignType: TypeCampaignEnum; operatorId: string | null }, context: SetupContext) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);
    const formValidationErrors = ref(props.formValidation);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const isEdit = ref(!!props.modelValue.custom_title);

    const commonFields = ref({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: asInt(props.modelValue.outputs_number),
      initial_number_outputs: props.modelValue.initial_number_outputs,
    });
    const grantAdditionalDelay = ref(props.modelValue.grant_additional_delay);
    const delay = ref<Period>({
      unit: props.modelValue.extra_time ? props.modelValue.extra_time.unit : 'DAY',
      value: props.modelValue.extra_time ? props.modelValue.extra_time.value : 1,
    });
    const options = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];
    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
    ];

    const includeSegments: Ref<FilterSegment<CurrentCartFilters>[]> = ref([]);
    const excludeSegments: Ref<FilterSegment<CurrentCartFilters>[]> = ref([]);
    const activeIncludeSegments = computed(() => getActiveSegments<CurrentCartFilters>(props.modelValue.inclusion, includeSegments.value));
    const activeExcludeSegments = computed(() => getActiveSegments<CurrentCartFilters>(props.modelValue.exclusion, excludeSegments.value));

    const filters: Ref<{ include: CurrentCartFilters; exclude: CurrentCartFilters }> = ref({
      include: { ...props.modelValue.inclusion },
      exclude: { ...props.modelValue.exclusion },
    });

    const activeItemsFilterPanel = computed(() => ({
      include: activeIncludeSegments.value.length,
      exclude: activeExcludeSegments.value.length,
    }));

    onBeforeMount(async () => {
      includeSegments.value = await getSegments();
      excludeSegments.value = await getSegments();
    });

    const update = () => context.emit('update:modelValue', createDefaultData({
      ...commonFields.value,
      grant_additional_delay: grantAdditionalDelay.value,
      extra_time: delay.value,
      inclusion: filters.value.include,
      exclusion: filters.value.exclude,
    }));

    const handleAddSegment = (type: keyof { include: CurrentCartFilters; exclude: CurrentCartFilters },
      segment: FilterSegment<CurrentCartFilters>) => {
      filters.value[type] = {
        ...filters.value[type],
        [segment.id]: { ...addInArraySegment<CurrentCartFilters>(segment, filters.value[type][segment.id]) },
      };
      update();
    };

    const handleRemoveSegment = (type: keyof { include: CurrentCartFilters; exclude: CurrentCartFilters },
      segmentName: keyof CurrentCartFilters, number: number) => {
      filters.value[type] = removeInSegment<CurrentCartFilters>(filters.value[type], segmentName, number);
      update();
    };

    watch([commonFields, grantAdditionalDelay, delay], update, { deep: true });

    return {
      t,
      componentFieldErrorsKey,
      formValidationErrors,
      isEdit,
      commonFields,
      grantAdditionalDelay,
      options,
      delay,
      units,
      includeSegments,
      excludeSegments,
      activeIncludeSegments,
      activeExcludeSegments,
      filters,
      handleAddSegment,
      handleRemoveSegment,
      activeItemsFilterPanel,
    };
  },
});
